import { useEffect } from 'react';

interface PageTitleOptions {
  title: string;
  description?: string;
  suffix?: string;
}

const DEFAULT_TITLE = 'Drawing Score Evaluator';
const DEFAULT_DESCRIPTION = 'AI-powered drawing assessment system that automatically evaluates and scores drawing submissions using advanced OCR and machine learning technologies.';

export const usePageTitle = ({ title, description, suffix }: PageTitleOptions) => {
  useEffect(() => {
    const fullTitle = suffix ? `${title} - ${suffix}` : `${title} - ${DEFAULT_TITLE}`;
    document.title = fullTitle;

    // Update meta description if provided
    if (description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = description;
        document.head.appendChild(meta);
      }
    }

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = DEFAULT_TITLE;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', DEFAULT_DESCRIPTION);
      }
    };
  }, [title, description, suffix]);
};

// Predefined page configurations
export const PAGE_CONFIGS = {
  dashboard: {
    title: 'Dashboard',
    description: 'Overview of drawing evaluation system with statistics and recent results.',
  },
  results: {
    title: 'Results',
    description: 'Browse and filter all drawing evaluation results with detailed scoring information.',
  },
  resultDetail: {
    title: 'Result Details',
    description: 'Detailed view of individual drawing evaluation with comprehensive scoring breakdown.',
  },
  processing: {
    title: 'Processing Status',
    description: 'Monitor file processing status and system operations in real-time.',
  },
  notFound: {
    title: 'Page Not Found',
    description: 'The requested page could not be found.',
  },
} as const;

export type PageConfig = keyof typeof PAGE_CONFIGS;